import DOMPurify from 'dompurify';

export default class FileUpload {
  constructor(el) {
    this.$el = $(el);
    this.elements = {
      fileInput: this.$el.find('#file-upload'),
      fileList: this.$el.find('.list-file'),
      errorMessage: this.$el.find('.file-error-message'),
      formGroup: this.$el.closest('.form-group'),
    };

    this.uploadedFiles = [];

    // Get configuration from input attributes
    this.maxFileSize = (this.elements.fileInput.attr('max-file-size') || 10) * 1024 * 1024;
    this.maxFiles = this.elements.fileInput.attr('max-files');
    this.allowedTypes = this.elements.fileInput.attr('allowed-types')?.trim().split(',').map((type) => type.trim());
    this.isRequired = this.elements.fileInput.hasClass('required');

    // Get error message templates and replace placeholders
    const fileSizeInMB = Math.floor(this.maxFileSize / (1024 * 1024));
    const allowedTypesDisplay = this.allowedTypes.map((type) => type.toUpperCase().replace('.', '')).join(', ');

    this.fileLargeErrorMessage = (this.elements.fileInput.attr('file-large-error-message') || '')
      .replace('{{fileSize}}', fileSizeInMB);
    this.fileTypeErrorMessage = (this.elements.fileInput.attr('file-type-error-message') || '')
      .replace('{{allowedTypes}}', allowedTypesDisplay);

    // Text constants - will be moved to CMS later
    this.TEXT = {
      MAX_FILES_ERROR: `You can only upload up to ${this.maxFiles} files`,
      FILE_SIZE_ERROR: (fileNames) => `${this.fileLargeErrorMessage}: ${fileNames.join(', ')}`,
      FILE_TYPE_ERROR: (fileNames) => `${this.fileTypeErrorMessage}: ${fileNames.join(', ')}`,
      REMOVE_FILE_LABEL: 'remove file',
      VIDEO_IMAGE_ALT: 'video or image',
      INVALID_FILE_TYPE: 'Invalid file type',
      REQUIRED_ERROR: this.elements.errorMessage.text() || 'At least one photo is required',
      DEFAULT_THUMB: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAABAQMAAADO7O3JAAAAA1BMVEUAAACnej3aAAAAAXRSTlMAQObYZgAAAApJREFUCNdjYAAAAAIAAeIhvDMAAAAASUVORK5CYII=',
      LOADING_TEXT: 'Uploading...',
    };

    this.elements.template = this.createTemplate();
    this.init();
  }

  init() {
    // Add multiple attribute to file input
    this.elements.fileInput.attr('multiple', true);
    this.bindEvents();
  }

  bindEvents() {
    this.elements.fileInput.on('change', this.handleFileSelect.bind(this));
    this.elements.fileInput.on('click', this.handleFileInputClick.bind(this));
    this.$el.on('click', '.remove-file', this.handleFileRemove.bind(this));
  }

  showError(message) {
    if (this.elements.errorMessage.length) {
      this.elements.errorMessage.text(DOMPurify.sanitize(message)).removeClass('hidden');
      this.elements.formGroup.addClass('invalid');
    }
  }

  hideError() {
    if (this.elements.errorMessage.length) {
      this.elements.errorMessage.addClass('hidden');
      this.elements.formGroup.removeClass('invalid');
    }
  }

  handleFileInputClick() {
    // Store the current number of files
    this.filesCountBeforeClick = this.uploadedFiles.length;
  }

  handleFileSelect({ target }) {
    const { files } = target;

    // Show required message if no files were selected and no files in the list
    if ((!files || !files.length) && this.isRequired && this.uploadedFiles.length === 0) {
      this.showError(this.TEXT.REQUIRED_ERROR);
      return;
    }

    if (!files || !files.length) { return; }

    // Check if adding these files would exceed the limit
    if (this.uploadedFiles.length + files.length > this.maxFiles) {
      this.showError(this.TEXT.MAX_FILES_ERROR);
      target.value = '';
      return;
    }

    const fileArray = Array.from(files);
    const invalidSizeFiles = [];
    const invalidTypeFiles = [];
    const validFiles = [];

    fileArray.forEach((file) => {
      const fileName = file.name.toLowerCase();
      if (file.size > this.maxFileSize) {
        invalidSizeFiles.push(file.name);
      } else if (!this.allowedTypes.some((ext) => fileName.endsWith(ext.toLowerCase()))) {
        invalidTypeFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    // If we have valid files, clear any previous errors first
    if (validFiles.length > 0) {
      this.hideError();
    }

    // Show errors if any
    if (invalidSizeFiles.length) {
      this.showError(this.TEXT.FILE_SIZE_ERROR(invalidSizeFiles));
    }
    if (invalidTypeFiles.length) {
      this.showError(this.TEXT.FILE_TYPE_ERROR(invalidTypeFiles));
    }

    // Add valid files to the list
    validFiles.forEach((file) => {
      this.uploadedFiles.push(file);
      this.addFileToList(file);
    });

    // Reset input to allow selecting same files again
    target.value = '';
  }

  validateFile(file) {
    if (!file) { return false; }

    if (file.size > this.maxFileSize) {
      return false;
    }

    const fileName = file.name.toLowerCase();
    const hasValidExtension = this.allowedTypes.some((ext) => fileName.endsWith(ext.toLowerCase()));
    return hasValidExtension;
  }

  addFileToList(file) {
    const isVideo = file.type.startsWith('video/');
    const date = new Date().toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const fileElement = this.elements.template
      .replace('{fileName}', DOMPurify.sanitize(file.name))
      .replace('{date}', date)
      .replace('{videoClass}', isVideo ? 'flex items-center justify-center' : '')
      .replace('{playButton}', isVideo ? '<span aria-hidden="true" class="icomoon icon-play-2 bg-white flex items-center justify-center w-14 h-14 rounded-full absolute transform-center-middle"></span>' : '');

    const $fileElement = $(fileElement);

    // Store file reference in the DOM element
    $fileElement.data('file', file);

    // Create thumbnail
    if (file.type.startsWith('image/')) {
      this.createImageThumbnail(file, $fileElement);
    } else if (file.type.startsWith('video/')) {
      this.createVideoThumbnail(file, $fileElement);
    } else {
      // Do nothing for now
    }

    this.elements.fileList.append($fileElement);
  }

  createImageThumbnail(file, $element) {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target && e.target.result) {
        const img = $element.find('img');
        const newImg = new Image();
        newImg.onload = () => {
          img.attr('src', e.target.result);
          $element.find('.loading-overlay').fadeOut(200);
        };
        newImg.src = e.target.result;
      }
    };
    reader.readAsDataURL(file);
  }

  createVideoThumbnail(file, $element) {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      if (video.duration === Infinity) {
        video.currentTime = 1;
      }
      video.currentTime = 1;
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailUrl = canvas.toDataURL();
          const img = $element.find('img');
          const newImg = new Image();
          newImg.onload = () => {
            img.attr('src', thumbnailUrl);
            $element.find('.loading-overlay').fadeOut(200);
          };
          newImg.src = thumbnailUrl;
        }
      } catch (error) {
        console.error('Error creating video thumbnail:', error);
        $element.find('.loading-overlay').fadeOut(200);
      } finally {
        URL.revokeObjectURL(video.src);
      }
    };

    video.onerror = () => {
      console.error('Error loading video file');
      URL.revokeObjectURL(video.src);
      $element.find('.loading-overlay').fadeOut(200);
    };

    const videoUrl = URL.createObjectURL(file);
    video.src = videoUrl;
  }

  handleFileRemove(e) {
    e.preventDefault();
    const $fileElement = $(e.currentTarget).closest('.box-content-file');
    const file = $fileElement.data('file');

    // Remove file from tracking array
    if (file) {
      const fileIndex = this.uploadedFiles.indexOf(file);
      if (fileIndex > -1) {
        this.uploadedFiles.splice(fileIndex, 1);
      }
    }

    $fileElement.remove();

    // Show error if no files are uploaded and it's required
    if (this.isRequired && this.uploadedFiles.length === 0) {
      this.showError(this.TEXT.REQUIRED_ERROR);
    }
  }

  // Get all uploaded files (useful for form submission)
  getFiles() {
    return this.uploadedFiles;
  }

  createTemplate() {
    return `
      <div class="relative bg-gray-900 text-left px-4 pb-4 text-xs box-content-file">
        <div class="relative pb-47p mb-4 -mx-4 {videoClass}">
          <img src="${this.TEXT.DEFAULT_THUMB}" alt="${this.TEXT.VIDEO_IMAGE_ALT}" class="w-full absolute left-0 top-0 h-full object-cover mb-2">
          <div class="loading-overlay absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div class="text-white">${this.TEXT.LOADING_TEXT}</div>
          </div>
          <button class="xl:opacity-0 absolute top-0 right-0 w-12 h-12 remove-file">
            <span class="icomoon icon-x" aria-hidden="true"></span>
            <span class="sr-only">${this.TEXT.REMOVE_FILE_LABEL}</span>
          </button>
          {playButton}
        </div>
        <p class="font-bold mb-0">{fileName}</p>
        <p class="text-gray-500">{date}</p>
      </div>
    `;
  }
}